import { SavedFilterType } from 'shared';

import { invoiceAgingBucketMenuLabel, invoiceAgingBuckets } from '../../lib/invoiceAgingBuckets';
import {
  actionQuickFilters,
  contactAgeRanges,
  escalationQuickFilters,
  useQuickFilterNavigation,
} from '../../hooks/useQuickFilterNavigation';
import { NestedNavType } from '../../hooks/useSidebarLayout';

import { OrgSavedFilterType, SavedFiltersNav } from './SavedFiltersNav';
import { EmailTemplateTypesNav } from './EmailTemplateTypesNav';
import { GroupsNav } from './GroupsNav';
import { ExportJobsNav } from './ExportJobsNav';
import { ContactQuickFiltersNav } from './ContactQuickFiltersNav';

const quickFilterClasses = `block w-full rounded-md px-2 py-1.5 text-left text-sm text-gray-600 hover:bg-gray-100 hover:text-gray-900`;

// Quick filters + saved filters for the invoice / contact / escalation table pages
function FilterNav({ type }: { type: OrgSavedFilterType }) {
  const {
    navigateToInvoiceAgingBucket,
    navigateWithAgeFilter,
    navigateToEscalationQuickFilter,
    navigateToActionQuickFilter,
  } = useQuickFilterNavigation();

  function renderQuickFilters() {
    if (type === SavedFilterType.Invoice) {
      return invoiceAgingBuckets.map(bucket => (
        <button
          key={ bucket.key }
          type={ `button` }
          onClick={ () => navigateToInvoiceAgingBucket(bucket) }
          className={ quickFilterClasses }
        >
          { invoiceAgingBucketMenuLabel(bucket) }
        </button>
      ));
    }

    if (type === SavedFilterType.Escalation) {
      return escalationQuickFilters.map(filter => (
        <button
          key={ filter.key }
          type={ `button` }
          onClick={ () => navigateToEscalationQuickFilter(filter.key) }
          className={ quickFilterClasses }
        >
          { filter.label }
        </button>
      ));
    }

    if (type === SavedFilterType.Action) {
      return actionQuickFilters.map(filter => (
        <button
          key={ filter.key }
          type={ `button` }
          onClick={ () => navigateToActionQuickFilter(filter.key) }
          className={ quickFilterClasses }
        >
          { filter.label }
        </button>
      ));
    }

    return (
      <>
        { contactAgeRanges.map(range => (
          <button
            key={ range.key }
            type={ `button` }
            onClick={ () => navigateWithAgeFilter(`contacts`, range.key) }
            className={ quickFilterClasses }
          >
            { range.label }
          </button>
        )) }
        <ContactQuickFiltersNav buttonClassName={ quickFilterClasses } />
      </>
    );
  }

  return (
    <>
      <div className={ `px-2` }>
        <p className={ `px-2 text-xs font-semibold uppercase tracking-wider text-gray-400` }>
          { `Quick Filters` }
        </p>
        <div className={ `mt-1 space-y-1` }>
          { renderQuickFilters() }
        </div>
      </div>
      <SavedFiltersNav
        type={ type }
        theme={ `light` }
        variant={ `nested` }
      />
      { type === SavedFilterType.Invoice && <ExportJobsNav entity={ `invoice` } /> }
      { type === SavedFilterType.Contact && <ExportJobsNav entity={ `contact` } /> }
      { type === SavedFilterType.Action && <ExportJobsNav entity={ `action` } /> }
      { type === SavedFilterType.Escalation && <ExportJobsNav entity={ `escalation` } /> }
    </>
  );
}

// Secondary sidebar shown next to the main sidebar on pages with submenu
// items (quick filters + saved filters, email template types, or the org's
// groups), Intercom style. Sits inside the content column, below the top nav.
export function NestedSidebar({ type }: { type: NestedNavType }) {
  return (
    <div className={ `flex h-full w-fit max-w-[150px] flex-col overflow-y-auto border-r border-gray-200 bg-gray-50 pt-5 pb-4` }>
      { type === `emailTemplates` && <EmailTemplateTypesNav /> }
      { type === `groups` && <GroupsNav /> }
      { type !== `emailTemplates` && type !== `groups` && <FilterNav type={ type } /> }
    </div>
  );
}
